import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Instagram, MessageCircle, Facebook } from "lucide-react";
import { useContent } from "../lib/content";
import { buildWhatsAppLink } from "../lib/api";

const navLinks = [
  { to: "/", label: "خانه" },
  { to: "/products", label: "محصولات" },
  { to: "/articles", label: "مقالات" },
  { to: "/about", label: "درباره ما" },
  { to: "/contact", label: "تماس با ما" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { settings } = useContent();

  return (
    <header
      className="sticky top-0 z-50 glass border-b border-stone-200"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <div className="w-11 h-11 rounded-2xl honey-gradient flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
              <span className="text-white font-black text-lg">
                {(settings.brand_name_fa || "تحسین").charAt(0)}
              </span>
            </div>
            <div className="flex flex-col leading-tight">
              <span
                className="text-xl font-black text-stone-900"
                data-testid="header-brand-fa"
              >
                {settings.brand_name_fa || "تحسین"}
              </span>
              <span
                className="text-[11px] tracking-widest text-amber-700 uppercase"
                data-testid="header-brand-en"
              >
                {settings.tagline || "Tahsin"}
              </span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-[15px] transition-colors ${
                    isActive
                      ? "bg-amber-100 text-amber-800 font-semibold"
                      : "text-stone-700 hover:bg-stone-100"
                  }`
                }
                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {settings.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noreferrer"
                aria-label="اینستاگرام تحسین"
                className="w-10 h-10 rounded-full flex items-center justify-center text-stone-600 hover:text-amber-700 hover:bg-amber-50 transition-colors"
                data-testid="header-instagram-link"
              >
                <Instagram className="w-5 h-5" />
              </a>
            )}
            <a
              href={buildWhatsAppLink(
                settings.whatsapp_number,
                "سلام، برای سفارش محصولات تحسین پیام می‌دهم.",
              )}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold px-5 py-2.5 rounded-full shadow-sm transition-colors"
              data-testid="header-whatsapp-cta"
            >
              <MessageCircle className="w-4 h-4" />
              سفارش از واتساپ
            </a>
          </div>

          <button
            className="md:hidden w-11 h-11 flex items-center justify-center rounded-full hover:bg-stone-100"
            onClick={() => setOpen(!open)}
            aria-label="منوی موبایل"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-stone-200 bg-white"
          data-testid="mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base ${
                    isActive
                      ? "bg-amber-100 text-amber-800 font-semibold"
                      : "text-stone-700 hover:bg-stone-100"
                  }`
                }
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={buildWhatsAppLink(
                settings.whatsapp_number,
                "سلام، برای سفارش محصولات تحسین پیام می‌دهم.",
              )}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-3 rounded-full"
              data-testid="mobile-whatsapp-cta"
            >
              <MessageCircle className="w-4 h-4" />
              سفارش از واتساپ
            </a>
            <div className="flex items-center gap-2 pt-3">
              {settings.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-stone-300 text-stone-700 rounded-full py-3"
                  data-testid="mobile-instagram-link"
                >
                  <Instagram className="w-4 h-4" /> اینستاگرام
                </a>
              )}
              {settings.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-stone-300 text-stone-700 rounded-full py-3"
                  data-testid="mobile-facebook-link"
                >
                  <Facebook className="w-4 h-4" /> فیسبوک
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
